'use client';

import { Close } from '@mui/icons-material';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, TextField, Typography } from '@mui/material';
import { env } from '@prismo-io/env';
import { SupabaseImage } from '@prismo-io/utils/client/supabase-image';
import { useAction } from 'next-safe-action/hooks';
import { useRouter } from 'next/navigation';
import { type FC, useCallback, useMemo, useState } from 'react';
import { useInterval } from 'usehooks-ts';
import { useWindowOpener } from '../../utils/use-window-opener';
import { OAuth2CancelAction } from './oauth2-cancel.action';
import { OAuth2Action } from './oauth2.action';
type FormItem = {
  type: 'input';
  name: string;
  label: string;
  placeholder?: string;
  required?: boolean;
};
type Action = {
  kind: 'OAUTH2';
  button: {
    text: string;
  };
  form?: FormItem[];
};
type Tools = {
  id: string;
  logo?: string | null;
  name: string;
  function_url: string;
};
type Props = {
  tool: Tools;
  action: Action;
  debug?: boolean;
  onComplete?: () => void;
};
export const OAuth2Button: FC<Props> = props => {
  const {
    tool,
    action,
    onComplete = undefined,
    debug = false
  } = props;
  const router = useRouter();
  const {
    open,
    close
  } = useWindowOpener();
  const [isAuthenticating, setAuthenticating] = useState<boolean>(false);
  const {
    execute,
    status
  } = useAction(OAuth2Action, {
    onSuccess: result => {
      if (result.data && typeof window !== 'undefined') {
        const {
          goTo,
          searchParams
        } = result.data;
        setAuthenticating(true);
        const goToUrl = goTo.startsWith('http') ? new URL(goTo) : new URL(goTo, window.location.href);
        for (const key in searchParams) {
          goToUrl.searchParams.set(key, searchParams[key]);
        }
        goToUrl.searchParams.set('redirectTo', window.location.href);
        open({
          name: tool.id,
          url: goToUrl.toString(),
          features: {
            popup: true,
            width: 500,
            height: 600
          },
          onBlocked: () => {
            window.location.href = goToUrl.toString();
          },
          onUnload: () => {
            setAuthenticating(false);
            router.refresh();
            if (onComplete) {
              onComplete();
            }
          }
        });
      }
    }
  });
  const {
    execute: cancelAction
  } = useAction(OAuth2CancelAction, {
    onSuccess: result => {
      setAuthenticating(false);
      router.refresh();
      close(tool.id);
    }
  });
  const formItems = useMemo((): FormItem[] => action?.form ?? [], [action?.form]);
  const [formState, setFormState] = useState<Record<string, string>>({});
  const formValid = useMemo((): boolean => formItems.filter(item => item.required ?? false).map(item => formState[item.name]).map(value => value ? value.trim() !== '' : false).every(valid => valid), [formItems, formState]);
  const onRun = useCallback(() => {
    execute({
      tool: {
        id: tool.id,
        functionUrl: tool.function_url
      },
      redirectTo: window.location.href,
      ...formState
    });
  }, [execute, tool, formState]);
  const onCancel = useCallback(() => {
    cancelAction({
      tool: {
        id: tool.id
      }
    });
  }, [cancelAction, tool]);
  useInterval(() => {
    onCancel();
  }, isAuthenticating ? 120_000 : null);
  return <>
      <Stack component="form" gap={2} data-sentry-element="Stack" data-sentry-source-file="oauth2.button.tsx">
        <input name="tool.id" type="text" value={tool.id} readOnly disabled={status === 'executing'} hidden={!debug && env.NEXT_PUBLIC_APP_ENV !== 'development'} />
        <input name="tool.functionUrl" type="text" value={tool.function_url} readOnly disabled={status === 'executing'} hidden={!debug && env.NEXT_PUBLIC_APP_ENV !== 'development'} />
        <input name="redirectTo" type="text" value={window.location.href} readOnly disabled={status === 'executing'} hidden={!debug && env.NEXT_PUBLIC_APP_ENV !== 'development'} />
        {formItems.map(item => <TextField name={item.name} label={item.label} placeholder={item?.placeholder} required={item?.required ?? false} disabled={status === 'executing'} value={formState[item.name]} onChange={evt => setFormState(prev => ({
        ...prev,
        [item.name]: evt.target.value ?? ''
      }))} />)}
        <Button type="submit" disabled={!formValid || status === 'executing'} onClick={onRun} data-sentry-element="Button" data-sentry-source-file="oauth2.button.tsx">
          {action.button.text}
        </Button>
      </Stack>
      <Dialog open={isAuthenticating} data-sentry-element="Dialog" data-sentry-source-file="oauth2.button.tsx">
        <DialogTitle data-sentry-element="DialogTitle" data-sentry-source-file="oauth2.button.tsx">
          <Stack direction="row" gap={1} alignItems="center" height="3rem" data-sentry-element="Stack" data-sentry-source-file="oauth2.button.tsx">
            <Box component="div" position="relative" data-sentry-element="Box" data-sentry-source-file="oauth2.button.tsx">
              <SupabaseImage src={tool.logo} alt={tool.name} style={{
              objectFit: 'contain',
              objectPosition: 'center'
            }} fill bucket="tsid" replaceOnError={tool.name} data-sentry-element="SupabaseImage" data-sentry-source-file="oauth2.button.tsx" />
            </Box>
            Authentification
          </Stack>
        </DialogTitle>
        <IconButton aria-label="close" onClick={onCancel} sx={{
        position: 'absolute',
        right: 8,
        top: 8
      }} data-sentry-element="IconButton" data-sentry-source-file="oauth2.button.tsx">
          <Close data-sentry-element="Close" data-sentry-source-file="oauth2.button.tsx" />
        </IconButton>
        <DialogContent data-sentry-element="DialogContent" data-sentry-source-file="oauth2.button.tsx">
          <Stack gap={1} alignItems="center" data-sentry-element="Stack" data-sentry-source-file="oauth2.button.tsx">
            <CircularProgress size="24px" data-sentry-element="CircularProgress" data-sentry-source-file="oauth2.button.tsx" />
            <Typography fontWeight="bolder" data-sentry-element="Typography" data-sentry-source-file="oauth2.button.tsx">
              Authentification en cours...
            </Typography>
            <Typography textAlign="center" maxWidth="34rem" whiteSpace="pre-line" data-sentry-element="Typography" data-sentry-source-file="oauth2.button.tsx">
              Cette fenêtre se fermera automatiquement lorsque la connexion sera
              terminée.
              <br />
              Assurez-vous que votre navigateur ne bloque pas les nouvelles
              fenêtres.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions data-sentry-element="DialogActions" data-sentry-source-file="oauth2.button.tsx">
          <Button onClick={onCancel} variant="outlined" data-sentry-element="Button" data-sentry-source-file="oauth2.button.tsx">
            Annuler
          </Button>
        </DialogActions>
      </Dialog>
    </>;
};